import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ControlledModal from '../Modal/ControlledModal';
import { Button } from '../ui/button';
import { useRegisterTalentMutation } from '../../reactWrapper/Redux/apiSlice';

/**
 * Register one talent.
 *
 * The same thing the bulk upload does, for one person: an account with no
 * password anyone is told, and a branded email carrying this message and a
 * link to set their own. The server writes a batch of one, so a single
 * registration is as traceable as a sheet of eight hundred.
 */

const DEFAULT_SUBJECT = 'Your Droomwork account is ready';
const DEFAULT_MESSAGE = `Hi {{firstName}},

We have set up a Droomwork account for you so you can be matched with roles that fit your experience.

Set your password using the button below, then complete your profile so we can start matching you.`;

const DEFAULT_CTA = 'Set your password';

/**
 * The readable part of a failed request. The API puts its own message in
 * data.error.message; the rest are what RTK Query gives us when it never
 * got that far.
 */
function describeError(err) {
  if (err && err.data && err.data.error && err.data.error.message) {
    return err.data.error.message;
  }
  if (err && err.data && err.data.message) {
    return err.data.message;
  }
  if (err && err.error) {
    return err.error;
  }
  return 'Something went wrong';
}

const RegisterTalentModal = ({ open, onClose, onRegistered }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_CTA);
  const [error, setError] = useState(null);

  const [register, { isLoading }] = useRegisterTalentMutation();

  /* Start clean each time it opens, so a failed attempt is not still on
     screen when the admin comes back to register somebody else. */
  useEffect(() => {
    if (!open) return;
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setSubject(DEFAULT_SUBJECT);
    setMessage(DEFAULT_MESSAGE);
    setCtaLabel(DEFAULT_CTA);
    setError(null);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const out = await register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        cta_label: ctaLabel.trim(),
      }).unwrap();

      toast.success(out?.message || 'Talent registered');
      onRegistered?.();
      onClose?.();
    } catch (err) {
      setError(describeError(err));
    }
  };

  // Every field the server insists on, and not already sending.
  let canSubmit = true;

  if (isLoading) {
    canSubmit = false;
  } else if (email.trim() === '') {
    canSubmit = false;
  } else if (subject.trim() === '') {
    canSubmit = false;
  } else if (message.trim() === '') {
    canSubmit = false;
  }

  return (
    <ControlledModal
      open={open}
      closeModal={() => !isLoading && onClose?.()}
      closeOnDocumentClick={!isLoading}
      contentStyle={{
        width: 'min(560px, 97vw)',
        borderRadius: '12px',
        padding: 0,
        border: 'none',
        background: 'transparent',
      }}
    >
      <form onSubmit={submit} className='flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-card'>
        <div className='flex items-center justify-between border-b border-border px-6 py-4'>
          <h2 className='text-lg font-bold text-brand-navy'>Register a talent</h2>
          <button
            type='button'
            onClick={() => onClose?.()}
            disabled={isLoading}
            className='text-sm text-muted-foreground hover:text-foreground'
          >
            Close
          </button>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5'>
          <p className='text-sm text-muted-foreground'>
            They will get an email with this message and a link to set their own password. No
            password is sent, and nothing is created until you press Register.
          </p>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <label className='flex flex-col gap-1 text-sm'>
              <span className='font-medium text-foreground'>First name</span>
              <input
                type='text'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                className='h-10 rounded-md border border-input bg-background px-3 text-sm'
              />
            </label>
            <label className='flex flex-col gap-1 text-sm'>
              <span className='font-medium text-foreground'>Last name</span>
              <input
                type='text'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                className='h-10 rounded-md border border-input bg-background px-3 text-sm'
              />
            </label>
          </div>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>Phone</span>
            <input
              type='tel'
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isLoading}
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>
              Email <span className='text-red-600'>*</span>
            </span>
            <input
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>
              Subject <span className='text-red-600'>*</span>
            </span>
            <input
              type='text'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isLoading}
              maxLength={150}
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>
              Message <span className='text-red-600'>*</span>
            </span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading}
              rows={8}
              className='rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
            <span className='text-xs text-muted-foreground'>
              {'Plain text. Leave a blank line between paragraphs. Use {{firstName}}, '}
              {'{{lastName}} or {{fullName}} — single braces will not be replaced. Do not '}
              paste a link; the button below the message is added for you.
            </span>
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>Button wording</span>
            <input
              type='text'
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              disabled={isLoading}
              maxLength={80}
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
            <span className='text-xs text-muted-foreground'>
              Only the wording changes — the button always opens their set-password page.
            </span>
          </label>

          {error ? (
            <p
              role='alert'
              className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
            >
              {error}
            </p>
          ) : null}
        </div>

        <div className='flex items-center justify-end gap-2 border-t border-border px-6 py-4'>
          <Button type='submit' disabled={!canSubmit} className='bg-brand-navy text-white hover:bg-brand-navy/90'>
            {isLoading ? 'Registering…' : 'Register and send invite'}
          </Button>
        </div>
      </form>
    </ControlledModal>
  );
};

export default RegisterTalentModal;
